import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Building2, RefreshCw, UserPlus2 } from 'lucide-react'
import { cn } from '@/lib/utils'
import { toast } from 'sonner'
import type { ApiError } from '../../../lib/api'
import { useIacrmClients, useSyncIacrmClients } from '../../iacrm/hooks'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Field, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

type InviteMode = 'existing' | 'new'

interface AddAgentDialogProps {
  open: boolean
  isPending: boolean
  error: ApiError | null
  onClose: () => void
  onSubmit: (payload: { display_name: string; email: string; notes?: string }) => void
}

export function AddAgentDialog({
  open,
  isPending,
  error,
  onClose,
  onSubmit,
}: AddAgentDialogProps) {
  const { t } = useTranslation()
  const [step, setStep] = useState<'select' | 'form'>('select')
  const [mode, setMode] = useState<InviteMode | null>(null)
  const [selectedClientId, setSelectedClientId] = useState('')
  const [displayName, setDisplayName] = useState('')
  const [email, setEmail] = useState('')
  const [notes, setNotes] = useState('')

  const clientsQuery = useIacrmClients()
  const syncClientsMutation = useSyncIacrmClients()
  const activeClients = (clientsQuery.data?.data ?? []).filter((client) => client.status === 'active')
  const existingClientSubmitBlocked = mode === 'existing' && !selectedClientId

  function reset() {
    setStep('select')
    setMode(null)
    setSelectedClientId('')
    setDisplayName('')
    setEmail('')
    setNotes('')
  }

  function handleClose() {
    reset()
    onClose()
  }

  function handleContinue() {
    if (!mode) return
    if (mode === 'new') {
      setDisplayName('')
      setEmail('')
    }
    setStep('form')
  }

  function handleClientSelect(clientId: string) {
    setSelectedClientId(clientId)
    const client = activeClients.find((entry) => entry.iacrm_id === clientId)
    if (client) {
      setDisplayName(client.contact_name)
      setEmail(client.contact_email ?? '')
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onSubmit({
      display_name: displayName.trim(),
      email: email.trim(),
      notes: notes.trim() || undefined,
    })
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose() }}>
      <DialogContent className="sm:max-w-lg" showCloseButton>
        <DialogHeader>
          <DialogTitle>{t('agents.dialogs.addAgent.title')}</DialogTitle>
          <DialogDescription>
            {t('agents.dialogs.addAgent.description')}
          </DialogDescription>
        </DialogHeader>

        {step === 'select' ? (
          <>
            <div className="grid grid-cols-2 gap-3 py-1">
              <Card
                role="button"
                tabIndex={0}
                aria-pressed={mode === 'existing'}
                onClick={() => setMode('existing')}
                onKeyDown={(e) => e.key === 'Enter' && setMode('existing')}
                className={cn(
                  'cursor-pointer border-2 transition-colors select-none',
                  mode === 'existing'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/40',
                )}
              >
                <CardContent className="flex flex-col gap-3 p-4">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                    <Building2 className="size-4 text-primary" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm leading-snug font-semibold text-foreground">
                      {t('agents.dialogs.addAgent.modes.existing.title')}
                    </p>
                    <p className="text-xs leading-5 text-muted-foreground">
                      {t('agents.dialogs.addAgent.modes.existing.description')}
                    </p>
                  </div>
                </CardContent>
              </Card>

              <Card
                role="button"
                tabIndex={0}
                aria-pressed={mode === 'new'}
                onClick={() => setMode('new')}
                onKeyDown={(e) => e.key === 'Enter' && setMode('new')}
                className={cn(
                  'cursor-pointer border-2 transition-colors select-none',
                  mode === 'new'
                    ? 'border-primary bg-primary/5'
                    : 'border-border hover:border-primary/40',
                )}
              >
                <CardContent className="flex flex-col gap-3 p-4">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10">
                    <UserPlus2 className="size-4 text-primary" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm leading-snug font-semibold text-foreground">
                      {t('agents.dialogs.addAgent.modes.new.title')}
                    </p>
                    <p className="text-xs leading-5 text-muted-foreground">
                      {t('agents.dialogs.addAgent.modes.new.description')}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" size="sm" onClick={handleClose}>
                {t('agents.dialogs.addAgent.cancel')}
              </Button>
              <Button type="button" size="sm" disabled={!mode} onClick={handleContinue}>
                {t('agents.dialogs.addAgent.continue')}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-1">
              {mode === 'existing' ? (
                <Field>
                  <div className="flex items-center justify-between">
                    <FieldLabel>{t('agents.dialogs.addAgent.iacrmClient.label')}</FieldLabel>
                    <SyncIacrmButton
                      tooltip={t('agents.dialogs.addAgent.iacrmClient.syncTooltip')}
                      onSync={() => syncClientsMutation.mutate(undefined, {
                        onSuccess: (data) => {
                          const r = data.result
                          toast.success(`${r.created} créé(s), ${r.updated} mis à jour`)
                        },
                        onError: () => toast.error('Échec de la synchronisation'),
                      })}
                      isPending={syncClientsMutation.isPending}
                    />
                  </div>
                  {clientsQuery.isPending ? (
                    <p className="text-sm text-muted-foreground">{t('agents.dialogs.addAgent.iacrmClient.loading')}</p>
                  ) : activeClients.length === 0 ? (
                    <p className="rounded-lg border border-dashed border-border bg-muted/20 px-4 py-3 text-sm text-muted-foreground">
                      {t('agents.dialogs.addAgent.iacrmClient.empty')}
                    </p>
                  ) : (
                    <Select value={selectedClientId} onValueChange={handleClientSelect} required>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder={t('agents.dialogs.addAgent.iacrmClient.placeholder')} />
                      </SelectTrigger>
                      <SelectContent>
                        {activeClients.map((client) => (
                          <SelectItem key={client.iacrm_id} value={client.iacrm_id}>
                            {client.contact_name}
                            {client.company_name ? ` · ${client.company_name}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </Field>
              ) : null}

              <Field>
                <FieldLabel htmlFor="invite-display-name">{t('agents.dialogs.addAgent.displayName.label')}</FieldLabel>
                <Input
                  id="invite-display-name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={t('agents.dialogs.addAgent.displayName.placeholder')}
                  autoComplete="name"
                  required
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="invite-email">{t('agents.dialogs.addAgent.email.label')}</FieldLabel>
                <Input
                  id="invite-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('agents.dialogs.addAgent.email.placeholder')}
                  autoComplete="email"
                  required
                />
                <p className="text-xs text-muted-foreground">
                  {t('agents.dialogs.addAgent.email.hint')}
                </p>
              </Field>

              <Field>
                <FieldLabel htmlFor="invite-notes">{t('agents.dialogs.addAgent.notes.label')}</FieldLabel>
                <Input
                  id="invite-notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t('agents.dialogs.addAgent.notes.placeholder')}
                />
              </Field>

              {error ? (
                <p className="text-sm text-destructive" role="alert">
                  {error.message}
                </p>
              ) : null}
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setStep('select')}
                disabled={isPending}
              >
                {t('agents.dialogs.addAgent.back')}
              </Button>
              <Button type="submit" size="sm" disabled={isPending || existingClientSubmitBlocked}>
                {isPending ? t('agents.dialogs.addAgent.submitLoading') : t('agents.dialogs.addAgent.submit')}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  )
}

function SyncIacrmButton({
  tooltip,
  onSync,
  isPending,
}: {
  tooltip: string
  onSync: () => void
  isPending: boolean
}) {
  return (
    <TooltipProvider delayDuration={100}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7 text-amber-600 hover:text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:hover:bg-amber-950/30"
            onClick={onSync}
            disabled={isPending}
          >
            <RefreshCw className={cn('size-3.5', isPending && 'animate-spin')} />
          </Button>
        </TooltipTrigger>
        <TooltipContent side="top">{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
